import { Tag } from 'lucide-react'
import type { ActiveDiscount } from '@/lib/api/discount-types'
import { formatInEventZone, timeZoneLabel } from '@/lib/eventTime'
import { formatMenuListPrice, formatUntil, hasPassed, saleOf, useEndsClock, useRouteEvent } from './menu-price'

interface ActiveDiscountBadgeProps {
  item: { effective_price?: number; price?: number; discount_price?: number | null; active_discount?: ActiveDiscount | null }
  /**
   * How the surrounding list prints a price in menu units. Defaults to the
   * menu list's format for the page's event.
   */
  formatPrice?: (price: number) => string
}

/**
 * The price a schedule is charging right now, the list price struck through,
 * and a pill naming the schedule (staff get the name, anyone else
 * "Promotion") and when it ends. With no schedule charging (or once its
 * window has ended, until the list is fetched again) it shows the item's own
 * sale price the same way with a "Sale" pill, or the list price alone.
 */
export function ActiveDiscountBadge({ item, formatPrice }: ActiveDiscountBadgeProps) {
  const event = useRouteEvent()
  const discount = item.active_discount
  const now = useEndsClock([discount?.ends_at])
  const format = formatPrice ?? ((price: number) => formatMenuListPrice(price, event))
  if (!discount || hasPassed(discount.ends_at, now)) {
    const sale = saleOf(item)
    if (sale != null && item.price != null) {
      return (
        <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
          <span className="font-medium text-emerald-700 dark:text-emerald-400">{format(sale)}</span>
          <s className="text-neutral-400 dark:text-neutral-500" title="List price">{format(item.price)}</s>
          <span className="rounded-full px-2 py-0.5 text-xs font-medium bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300">
            Sale
          </span>
        </span>
      )
    }
    return discount && item.price != null ? <span className="font-medium">{format(item.price)}</span> : null
  }
  const charged = item.effective_price ?? item.price
  const struck = item.price != null && charged != null && charged < item.price ? item.price : null
  const label = discount.name || 'Promotion'
  const until = discount.ends_at ? formatUntil(discount.ends_at, event?.timezone) : null
  const zone = event?.timezone ? ` (${timeZoneLabel(event.timezone)})` : ''
  const title = `${label} · ${discount.ends_at
    ? `ends ${formatInEventZone(discount.ends_at, event?.timezone, 'MMM d, yyyy h:mm a')}${zone}`
    : 'no end time'}`

  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
      {charged != null && <span className="font-medium text-emerald-700 dark:text-emerald-400">{format(charged)}</span>}
      {struck != null && (
        <s className="text-neutral-400 dark:text-neutral-500" title="List price">
          {format(struck)}
        </s>
      )}
      <span
        title={title}
        className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium bg-emerald-100 text-emerald-800 dark:bg-emerald-900/25 dark:text-emerald-300"
      >
        <Tag className="h-3 w-3 shrink-0" aria-hidden="true" />
        <span className="max-w-[10rem] truncate">{label}</span>
        {until && <span className="opacity-75">until {until}</span>}
      </span>
    </span>
  )
}
